import { z } from 'zod';
import { MAX_EXPLAINED_CHANGES } from './diff';
import { CHANGE_KINDS, LANGUAGES } from './options';
import { MAX_TEXT_LENGTH } from './text';

/** Longest `before` or `after` of one change; a whole rewritten paragraph fits. */
export const MAX_CHANGE_TEXT_LENGTH = 2_000;
// A rewrite asked to be longer can outgrow the text it came from.
const MAX_REWRITE_LENGTH = MAX_TEXT_LENGTH * 3;

/** A text, one of its rewrites, and the changes between them: what Why and Alts work from. */
export const changedVersion = (changes: string) =>
  z.object({
    original: z.string().min(1).max(MAX_TEXT_LENGTH).describe('The text as the writer wrote it.'),
    rewrite: z.string().min(1).max(MAX_REWRITE_LENGTH).describe('One of its rewrites.'),
    changes: z
      .array(
        z.object({
          before: z.string().max(MAX_CHANGE_TEXT_LENGTH),
          after: z.string().max(MAX_CHANGE_TEXT_LENGTH),
        }),
      )
      .min(1)
      .max(MAX_EXPLAINED_CHANGES)
      .describe(changes),
  });

export const ExplainRequestSchema = changedVersion(
  'The edits to explain, in order: what a stretch of the original became.',
)
  .extend({
    language: z
      .enum(LANGUAGES)
      .exclude(['auto'])
      .optional()
      .describe(
        'The language to write the reasons in, such as the one the reader’s interface is in. English when left out.',
      ),
  })
  .meta({ id: 'ExplainRequest' });

export type ExplainRequest = z.infer<typeof ExplainRequestSchema>;

export const ExplanationSchema = z
  .object({
    change: z.int().nonnegative().describe('Index of the explained change in the request.'),
    kind: z.enum(CHANGE_KINDS),
    reason: z.string().trim().describe('Why the change helps, in a sentence.'),
  })
  .meta({ id: 'Explanation' });

export type Explanation = z.infer<typeof ExplanationSchema>;

export const ExplainResponseSchema = z
  .object({ explanations: z.array(ExplanationSchema) })
  .meta({ id: 'ExplainResponse' });

export type ExplainResponse = z.infer<typeof ExplainResponseSchema>;
